import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse, AxiosInstance } from "axios";
import { toast } from "react-toastify";

import UserData = App.Data.UserData;

interface SessionData {
    token: string | null;
    user: UserData | null;
    [key: string]: unknown;
}

interface ErrorResponse {
    errors?: Record<string, string[]>;
    message?: string;
    exception?: {
        errors?: Record<string, string[]>;
    };
}

export interface PaldockAxiosInstance extends AxiosInstance {
    setAuthorization: (token: string | null) => void;
}

declare module "axios" {
    export interface AxiosRequestConfig {
        /** Skip the global error toast for this request — the caller handles the error itself. */
        silentError?: boolean;
    }
}

// Base URL from environment variables
export const baseUrl: string = import.meta.env.VITE_API_URL;

const axiosInstance: PaldockAxiosInstance = axios.create({
    baseURL: baseUrl,
}) as PaldockAxiosInstance;

axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        if (import.meta.env.VITE_CONSOLE === "true") {
            console.log("Sending request to:", config.url);
        }
        return config;
    },
    (error: AxiosError): Promise<AxiosError> => {
        return Promise.reject(error);
    }
);

const handle401Error = () => {
    const sessionData = localStorage.getItem("session");
    if (sessionData) {
        const session: SessionData = JSON.parse(sessionData);
        localStorage.setItem(
            "session",
            JSON.stringify({
                ...session,
                token: null,
                user: null,
            })
        );
    }
    window.location.href = "/auth/login";
};

const handleErrorResponse = (error: AxiosError<ErrorResponse>) => {
    const responseData = error.response?.data as string | ErrorResponse;

    if (typeof responseData === "string" && responseData.startsWith("<")) {
        throw new Error(responseData);
    }

    if (typeof responseData === "object" && responseData !== null) {
        const errors = responseData.errors ?? responseData.exception?.errors;
        const message = responseData.message;

        if (errors) {
            Object.keys(errors).map((key) => {
                errors[key].map((errorItem) => {
                    toast.error(errorItem);
                });
            });
        } else if (message) {
            toast.error(message);
        }
    }
};

axiosInstance.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
        if (import.meta.env.VITE_CONSOLE === "true") {
            console.log("Received response from:", response.config.url);
        }
        return response;
    },
    (error: AxiosError<ErrorResponse>): Promise<AxiosError<ErrorResponse>> => {
        if (error.response?.status === 401) {
            handle401Error();
            return Promise.reject(error);
        }

        // 423 Locked is our own "resource intentionally locked" status (e.g. reports during a
        // workspace currency reconversion) — every place that can hit it already renders
        // dedicated UI for the locked state, so the generic toast would just be redundant noise
        // (and duplicated once per failed request on pages with several parallel queries).
        if (error.response?.status === 423) {
            return Promise.reject(error);
        }

        if (error.config?.silentError) {
            return Promise.reject(error);
        }

        if (error.response) {
            console.debug(error.response);
            try {
                handleErrorResponse(error);
            } catch (e) {
                return Promise.reject(new Error((e as Error).message));
            }
        } else {
            toast.error("An error occurred. Please try again later.");
        }

        return Promise.reject(error);
    }
);

axiosInstance.setAuthorization = (token: string | null) => {
    if (token) {
        axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
        delete axiosInstance.defaults.headers.common["Authorization"];
        delete axiosInstance.defaults.headers.common["Session"];
    }
};

export default axiosInstance as PaldockAxiosInstance;
